import { useMemo } from 'react';
import { Clock, Inbox } from 'lucide-react';

/**
 * Kanban board for the Interviewer dashboard — Phase 4.
 * Pure presentation: takes leads + stages + onCardClick, renders a horizontal
 * board of nine columns. No drag-and-drop in this phase.
 *
 * Stage tone mapping mirrors LeadsTable / LeadDetailModal:
 *  neutral · info · warning · success.
 */
const STAGE_TONE = {
  'Paid - Discovery Pending': 'paid',
  'New Lead':            'neutral',
  'Contacted':           'neutral',
  'Discovery Scheduled': 'info',
  'Discovery Completed': 'info',
  'Follow-Up Sent':      'info',
  'Selections Received': 'warning',
  'Interview Scheduled': 'warning',
  'Interview Completed': 'warning',
  'Sent to Production':  'success',
};

const TONE_BAR = {
  neutral: 'bg-slate-300',
  info:    'bg-blue-400',
  warning: 'bg-amber-400',
  success: 'bg-emerald-500',
  paid:    'bg-emerald-600',
};
const TONE_COUNT = {
  neutral: 'bg-slate-100 text-slate-600',
  info:    'bg-blue-50 text-blue-700',
  warning: 'bg-amber-50 text-amber-700',
  success: 'bg-emerald-50 text-emerald-700',
  paid:    'bg-emerald-100 text-emerald-800',
};
const TONE_COLUMN = {
  // 'paid' gets a soft emerald wash + left accent border to visually emphasize
  // high-priority, already-paid leads. Other stages keep the neutral slate.
  paid: 'border-emerald-200 bg-emerald-50/40 ring-1 ring-emerald-100',
};

function fmtRel(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '—';
  const diff = Date.now() - d.getTime();
  const day = 86400000;
  if (diff < day) return 'Today';
  if (diff < day * 2) return 'Yesterday';
  if (diff < day * 7) return `${Math.floor(diff / day)}d ago`;
  if (diff < day * 30) return `${Math.floor(diff / (day * 7))}w ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function PipelineBoard({ stages, leads, onCardClick }) {
  const grouped = useMemo(() => {
    const g = Object.fromEntries(stages.map(s => [s, []]));
    leads.forEach(l => {
      if (g[l.current_stage]) g[l.current_stage].push(l);
    });
    // Newest activity first within a column
    Object.keys(g).forEach(k => {
      g[k].sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));
    });
    return g;
  }, [stages, leads]);

  return (
    <div
      className="overflow-x-auto overflow-y-hidden pipeline-board-scroll"
      data-testid="pipeline-board"
    >
      <div className="inline-flex gap-3 px-6 py-5 min-h-[calc(100vh-112px)]">
        {stages.map(stage => {
          const tone = STAGE_TONE[stage] || 'neutral';
          const items = grouped[stage] || [];
          return (
            <section
              key={stage}
              data-testid={`stage-column-${stage.replace(/\s+/g, '-').toLowerCase()}`}
              className={`w-[290px] shrink-0 flex flex-col rounded-lg border bg-slate-50/60 overflow-hidden ${TONE_COLUMN[tone] || 'border-slate-200'}`}
            >
              {/* Tone bar */}
              <div className={`h-1 ${TONE_BAR[tone]}`} aria-hidden="true" />

              {/* Column header */}
              <header className="px-3 pt-2.5 pb-2 flex items-center gap-2 bg-white border-b border-slate-200">
                <h3 className="text-[12px] font-semibold text-slate-900 tracking-tight truncate flex-1">
                  {stage}
                </h3>
                <span
                  className={`ops-pill ${TONE_COUNT[tone]} border-transparent`}
                  data-testid={`stage-count-${stage.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {items.length}
                </span>
              </header>

              {/* Card list */}
              <div className="flex-1 p-2 space-y-1.5 overflow-y-auto">
                {items.length === 0 ? (
                  <div className="text-center py-8 px-2 flex flex-col items-center gap-1.5 text-slate-400">
                    <Inbox size={16} />
                    <p className="text-[11px]">No leads</p>
                  </div>
                ) : (
                  items.map(lead => (
                    <button
                      key={lead.id}
                      onClick={() => onCardClick(lead.id)}
                      data-testid={`lead-card-${lead.id}`}
                      className={`w-full text-left bg-white border rounded-md px-2.5 py-2 transition-all duration-150 group hover:shadow-sm ${
                        lead.payment_status === 'paid' ? 'border-emerald-300 hover:border-emerald-500' : 'border-slate-200 hover:border-slate-400'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-[13px] font-medium text-slate-900 truncate leading-tight flex-1">
                          {lead.client_first_name} {lead.client_last_name}
                        </p>
                        {lead.payment_status === 'paid' && (
                          <span className="ops-pill bg-emerald-100 text-emerald-800 border-emerald-200 px-1.5 text-[10px] shrink-0" data-testid={`paid-pill-${lead.id}`}>
                            Paid
                          </span>
                        )}
                      </div>
                      {lead.loved_one_first_name && (
                        <p className="text-[12px] text-slate-500 truncate leading-tight mt-0.5">
                          {lead.loved_one_first_name}
                        </p>
                      )}
                      <div className="flex items-center justify-between gap-2 mt-1.5">
                        <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
                          <Clock size={10} />
                          {fmtRel(lead.updated_at)}
                        </span>
                        {lead.interview_date && (
                          <span className="text-[11px] text-slate-500">
                            {new Date(lead.interview_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                          </span>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
